/**
 * DashboardTab — Trainer dashboard at-a-glance.
 *
 * KPIs: total trainees, active batches, today's attendance, average
 * attendance/assessment %, alert distribution, certification + handover
 * funnels, last-30-day attendance line, top-N at-risk trainees list.
 *
 * Batch filter narrows every panel. Selecting a batch reveals the
 * assessment-wise pass rates panel and the deeper batch view.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Users, Boxes, AlertTriangle, Award, TrendingUp, ChevronRight } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { TraineeFilterBar, useTraineeFilter, ALL } from './TraineeFilterContext';

const ALERT_COLORS = {
  Red: '#e11d48',
  Amber: '#f59e0b',
  Green: '#10b981',
  '—': '#94a3b8',
};

const PIE_COLORS = ['#1A6FF7', '#10b981', '#f59e0b', '#e11d48', '#8b5cf6', '#06b6d4', '#94a3b8'];

export default function DashboardTab() {
  const { get } = useApi();
  const { batchId, scopeQS } = useTraineeFilter();
  const [overview, setOverview] = useState(null);
  const [batchDash, setBatchDash] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const scope = scopeQS();
      const o = await get(`/trainees/dashboard/overview${scope ? `?${scope}` : ''}`);
      setOverview(o);
      // Deep per-batch view only when a single batch is selected.
      if (batchId !== ALL) {
        const b = await get(`/trainees/dashboard/batches/${batchId}`);
        setBatchDash(b);
      } else {
        setBatchDash(null);
      }
    } catch { /* shown via toast in interceptor */ }
    finally { setLoading(false); }
  }, [get, scopeQS, batchId]);

  useEffect(() => { load(); }, [load]);

  const alertChartData = useMemo(() => (
    (overview?.alert_distribution || []).map(c => ({
      name: c.key, value: c.count, fill: ALERT_COLORS[c.key] || '#94a3b8',
    }))
  ), [overview]);

  if (loading && !overview) {
    return <p className="text-sm text-muted-foreground p-8 text-center">Loading dashboard…</p>;
  }
  if (!overview) {
    return <p className="text-sm text-muted-foreground p-8 text-center">No data.</p>;
  }

  return (
    <div className="space-y-5" data-testid="dashboard-tab">
      {/* Filter bar */}
      <TraineeFilterBar />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Trainees" value={overview.active_trainees} icon={Users}
             hint={`${overview.total_trainees} total · ${overview.dropouts} dropped`} testid="kpi-trainees" />
        <Kpi label="Active batches" value={overview.active_batches} icon={Boxes}
             hint={`${overview.closed_batches} closed`} testid="kpi-batches" />
        <Kpi label="Today" value={`${overview.today_attendance_pct}%`} icon={TrendingUp}
             hint="Attendance today" testid="kpi-today" />
        <Kpi label="Avg attendance" value={`${overview.avg_attendance_pct}%`} icon={TrendingUp}
             hint="All-time" testid="kpi-avg-attendance" />
        <Kpi label="Avg assessment" value={`${overview.avg_assessment_pct}%`} icon={Award}
             hint="Weighted %" testid="kpi-avg-assessment" />
        <Kpi label="At risk" value={overview.at_risk.length} icon={AlertTriangle}
             hint="Red + Amber" testid="kpi-at-risk"
             accentClass={overview.at_risk.length > 0 ? 'text-rose-600' : ''} />
      </div>

      {/* 30-day attendance trend */}
      <Card className="p-4" data-testid="dashboard-attendance-trend">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-base font-semibold">Attendance trend — last 30 days</h3>
          <span className="text-xs text-muted-foreground">Daily aggregate %</span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={overview.last_30d_attendance}>
            <defs>
              <linearGradient id="att-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1A6FF7" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#1A6FF7" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis dataKey="date" tickFormatter={d => d.slice(5)} fontSize={11} />
            <YAxis domain={[0, 100]} fontSize={11} unit="%" />
            <Tooltip />
            <Area type="monotone" dataKey="value" stroke="#1A6FF7" fill="url(#att-grad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Funnels row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FunnelCard title="Alert status" data={alertChartData} testid="dashboard-alert-pie" />
        <FunnelCard title="Certification" data={toChart(overview.cert_funnel)} testid="dashboard-cert-funnel" />
        <FunnelCard title="Handover" data={toChart(overview.handover_funnel)} testid="dashboard-handover-funnel" />
      </div>

      {/* Batch-specific section */}
      {batchDash && (
        <Card className="p-4" data-testid="dashboard-batch-assessments">
          <h3 className="font-display text-base font-semibold mb-3">
            {batchDash.name} — Assessment pass rates
          </h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={batchDash.assessment_trend}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis domain={[0, 100]} fontSize={11} unit="%" />
              <Tooltip />
              <Legend />
              <Bar dataKey="avg_score_pct" name="Avg %" fill="#1A6FF7" />
              <Bar dataKey="pass_count" name="Pass count" fill="#10b981" />
              <Bar dataKey="attempts" name="Attempts" fill="#94a3b8" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* At risk list */}
      <Card data-testid="dashboard-at-risk">
        <div className="p-4 border-b">
          <h3 className="font-display text-base font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            Trainees needing intervention
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Anyone on Red or Amber, sorted by alert + ascending attendance.
          </p>
        </div>
        {overview.at_risk.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            All trainees are Green. Nice work.
          </p>
        ) : (
          <div className="divide-y">
            {overview.at_risk.map(t => (
              <div key={t.trainee_id} className="flex items-center justify-between p-3 hover:bg-muted/40"
                   data-testid={`at-risk-row-${t.trainee_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: ALERT_COLORS[t.alert_status] || '#94a3b8' }}
                  />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.employee_id ? `${t.employee_id} · ` : ''}{t.batch_name || '—'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Attendance</div>
                    <div className={`font-display tabular-nums font-semibold ${
                      t.attendance_pct < 80 ? 'text-rose-600' : ''
                    }`}>{t.attendance_pct}%</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Assessment</div>
                    <div className="font-display tabular-nums font-semibold">
                      {t.assessment_pct != null ? `${t.assessment_pct}%` : '—'}
                    </div>
                  </div>
                  {t.failing_assessments > 0 && (
                    <Badge variant="outline" className="text-rose-700 border-rose-300">
                      {t.failing_assessments} failed
                    </Badge>
                  )}
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, hint, icon: Icon, testid, accentClass = '' }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      </div>
      <p className={`font-display tabular-nums text-3xl font-bold ${accentClass}`}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1 truncate">{hint}</p>}
    </Card>
  );
}

function toChart(arr) {
  return (arr || []).map((c, i) => ({
    name: c.key, value: c.count, fill: PIE_COLORS[i % PIE_COLORS.length],
  }));
}

function FunnelCard({ title, data, testid }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Card className="p-4" data-testid={testid}>
      <h3 className="font-display text-base font-semibold mb-2">{title}</h3>
      {data.length === 0 || total === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No data yet.</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" outerRadius={70} innerRadius={36}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1 mt-2 text-xs">
            {data.map(d => (
              <div key={d.name} className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: d.fill }} />
                  {d.name}
                </span>
                <span className="font-display tabular-nums font-semibold">
                  {d.value} ({total ? Math.round(d.value / total * 100) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
